import { useCallback } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { inviteWithinOrg } from '@atlassian/invite-prompt';

import type { BulkInviteSuccessResponse, InviteUsersRequest } from './data-types';
import { getSiteAri, handleInviteUsersSuccessResponse } from './utilities';

const RECAPTCHA_VALIDATION_ERROR = 'Recaptcha validation failed';

export const useInviteUsers = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async ({
			userIds,
			continueUrl,
			tenantId,
			product,
		}: InviteUsersRequest): Promise<BulkInviteSuccessResponse> => {
			try {
				const origin = new OriginTracer({ product: 'confluence' });
				const continueUrlWithOrigin = origin.addToUrl(continueUrl);
				const originAttributes = origin.toAnalyticsAttributes({
					hasGeneratedId: true,
				});
				const inviteApiResponse = await inviteWithinOrg(
					userIds,
					continueUrlWithOrigin,
					tenantId,
					product,
					'confluence',
				);
				const response = handleInviteUsersSuccessResponse(inviteApiResponse);

				[...response.invited, ...response.accessRequested].forEach((invited) => {
					const resourceAri = getSiteAri(tenantId, product);
					const result = invited.results[resourceAri];
					const userId = invited.id;
					const analyticsAttributes = {
						...originAttributes,
						userId,
						products: [product],
						inviteResult: [
							{
								resourceAri,
								status: result,
							},
						],
						integration: 'invitePromptOnLink',
					};
					if (result === 'INVITED') {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'invited',
								actionSubject: 'user',
								source: 'inviteOnLinkInsert',
								attributes: analyticsAttributes,
							},
						}).fire();
					} else if (result === 'INVITED_PENDING_APPROVAL') {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'requested',
								actionSubject: 'productAccess',
								source: 'inviteOnLinkInsert',
								attributes: analyticsAttributes,
							},
						}).fire();
					} else {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'requested',
								actionSubject: 'invite',
								source: 'inviteOnLinkInsert',
								attributes: analyticsAttributes,
							},
						}).fire();
					}
				});

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'succeeded',
						actionSubject: 'inviteOnLinkInsert',
						source: 'inviteOnLinkInsert',
						attributes: {
							invited: response.invited.length,
							requested: response.accessRequested.length,
							errored: response.error.length,
						},
					},
				}).fire();

				return response;
			} catch (error: any) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject:
							error?.status === 400 && error?.body?.message === RECAPTCHA_VALIDATION_ERROR
								? 'inviteOnLinkInsert-recaptcha'
								: 'inviteOnLinkInsert',
						source: 'inviteOnLinkInsert',
						attributes: {
							message: String(error),
							details: { ...(error || {}) },
						},
					},
				}).fire();
				throw error;
			}
		},
		[createAnalyticsEvent],
	);
};
